// A small typed fetch wrapper for the Drape API (see docs/api.md).
// - always sends the session cookie (credentials: "include")
// - sends X-Drape-CSRF: 1 on every non-GET
// - parses {"error": {code, message}}
// - on 401 not_signed_in: POST /api/auth/guest once, then retries the request once
import type {
  Category,
  Check,
  CheckCreate,
  CheckSummary,
  DescribeIn,
  EventIn,
  Favorite,
  FavoriteCreate,
  FollowUpIn,
  Me,
  Meta,
  Preferences,
  Product,
  ReportIn,
  Trait,
  Unit,
} from "./types";

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

const GUEST_PATH = "/api/auth/guest";

async function parseError(res: Response): Promise<ApiError> {
  let code = "http_" + res.status;
  let message = "Something went wrong. Please try again.";
  try {
    const body: unknown = await res.json();
    const err = (body as { error?: { code?: unknown; message?: unknown } } | null)?.error;
    if (err && typeof err.code === "string") code = err.code;
    if (err && typeof err.message === "string" && err.message) message = err.message;
  } catch {
    /* not JSON */
  }
  return new ApiError(res.status, code, message);
}

export function createClient(opts: { fetch?: FetchLike; base?: string } = {}) {
  let fetchImpl: FetchLike = opts.fetch ?? ((u, i) => fetch(u, i));
  const base = opts.base ?? "";
  let guestInFlight: Promise<unknown> | null = null;

  async function raw(method: string, path: string, body?: unknown): Promise<Response> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (method !== "GET") headers["X-Drape-CSRF"] = "1";
    if (body !== undefined) headers["Content-Type"] = "application/json";
    return fetchImpl(base + path, {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  /** Create a guest session. Concurrent callers share one request. */
  function ensureGuest(): Promise<unknown> {
    if (!guestInFlight) {
      guestInFlight = request<Me>("POST", GUEST_PATH, undefined, false).finally(() => {
        guestInFlight = null;
      });
    }
    return guestInFlight;
  }

  async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
    const res = await raw(method, path, body);
    if (res.ok) {
      if (res.status === 204) return undefined as T;
      const text = await res.text();
      return (text ? JSON.parse(text) : undefined) as T;
    }
    const err = await parseError(res);
    if (retry && err.status === 401 && err.code === "not_signed_in" && path !== GUEST_PATH) {
      await ensureGuest();
      return request<T>(method, path, body, false);
    }
    throw err;
  }

  const get = <T>(p: string) => request<T>("GET", p);
  const post = <T>(p: string, b?: unknown) => request<T>("POST", p, b ?? {});
  const put = <T>(p: string, b: unknown) => request<T>("PUT", p, b);
  const del = <T>(p: string) => request<T>("DELETE", p);
  const enc = encodeURIComponent;

  return {
    setFetch(f: FetchLike) {
      fetchImpl = f;
    },
    request,

    // auth + me
    me: () => get<Me>("/api/me"),
    guest: () => post<Me>(GUEST_PATH),
    devSignIn: (name: string) => post<Me>("/api/auth/dev", { name }),
    logout: () => post<{ ok: true }>("/api/auth/logout"),
    loginUrl: (provider: string, next: string) =>
      `${base}/api/auth/login/${enc(provider)}?next=${enc(next)}`,
    putProfile: (b: { name?: string; height_cm?: number | null; unit?: Unit }) => put<Me>("/api/me/profile", b),
    preferences: () => get<Preferences>("/api/me/preferences"),
    putPreferences: (p: Preferences) => put<Preferences>("/api/me/preferences", p),
    traits: () => get<{ traits: Trait[] }>("/api/me/traits"),
    deleteTrait: (code: string) => del<{ ok: true }>(`/api/me/traits/${enc(code)}`),
    exportData: () => get<unknown>("/api/me/export"),
    deleteMe: () => del<{ ok: true }>("/api/me"),

    // meta + products
    meta: () => get<Meta>("/api/meta"),
    resolve: (url: string) => post<{ product: Product }>("/api/products/resolve", { url }),
    product: (id: string) => get<{ product: Product }>(`/api/products/${enc(id)}`),
    describe: (b: DescribeIn) => post<{ product: Product }>("/api/products/describe", b),
    addSizes: (id: string, b: { sizes: string[]; fit: string | null }) =>
      post<{ product: Product }>(`/api/products/${enc(id)}/sizes`, b),
    readSizeScreenshot: (id: string, image: string) =>
      post<{ sizes: string[]; fits: string[] }>(`/api/products/${enc(id)}/sizes/screenshot`, { image }),
    confirmSizes: (id: string, b: { sizes: string[]; fits: string[] }) =>
      post<{ product: Product }>(`/api/products/${enc(id)}/sizes/confirm`, b),

    // favorites
    favorites: () => get<{ favorites: Favorite[] }>("/api/favorites"),
    addFavorite: (b: FavoriteCreate) => post<{ favorite: Favorite }>("/api/favorites", b),
    deleteFavorite: (id: string) => del<{ ok: true }>(`/api/favorites/${enc(id)}`),
    readFitWishes: (b: { category: Category; text: string }) =>
      post<{ would_change: string[]; source: "claude" | "words" }>("/api/fit-wishes/read", b),

    // checks
    createCheck: (b: CheckCreate) => post<{ check: Check }>("/api/checks", b),
    checks: () => get<{ checks: CheckSummary[] }>("/api/checks"),
    check: (id: string) => get<{ check: Check }>(`/api/checks/${enc(id)}`),
    report: (id: string, b: ReportIn) => post<{ check: Check }>(`/api/checks/${enc(id)}/report`, b),
    followUp: (id: string, b: FollowUpIn) => post<{ check: Check }>(`/api/checks/${enc(id)}/follow-up`, b),
    event: (b: EventIn) => post<{ ok: true }>("/api/events", b),
  };
}

export type ApiClient = ReturnType<typeof createClient>;
export const api = createClient();

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return "We couldn't reach Drape. Check your connection and try again.";
}

